<template>
    <div v-if="!activeUserInfo.twoFactorAuthentication" class="pmc-right hide-scrollbar">
        <h1>
            2FA Setup <span></span>
        </h1>
        <p class="mt-8">Authenticator app</p>
        <p class="pmc-right-p1 mt-4">Authenticator apps and browser extensions like 1Password, Authy,
            Google Authenticator, etc. generate one-time passwords that are used as a second factor to
            verify your identity when prompted during sign-in.</p>
        <p class="mt-4">Scan QR code</p>
        <p class="pmc-right-p1">Use an authenticator app or browser extension to scan. </p>
        <div class="py-4 px-4 qr-code-container mt-4">
            <qrcode-vue :text="otpauthUrl" size="150" color="#000000" bg-color="#FFFFFF" error-level="L" />
        </div>
        <p class="pmc-right-p1 mt-4">Unable to scan? You can use the setup key to manually configure
            your authenticator app.</p>
        <div class="sec-code-key px-4 py-4 mt-4" @click="copyTextToClipboard(secret)">
            <p>Copy your 2FA secret code</p>
            <p class="pmc-right-p1 mt-2">{{ secret }}</p>
        </div>
        <form @submit.prevent="handleActivate2fa" class="ver-form mt-4">
            <div>
                <div class="flex flex-col">
                    <label>Verify the code from the app</label>
                    <input v-model="verificationCode" type="number" placeholder="xxxxxx" />
                    <p v-if="verificationCodeError" class="text-red-500 text-xs mt-1 font-mono">{{
                        verificationCodeError }}
                    </p>
                </div>
            </div>
            <button class="tfa-button mt-4" type="submit">
                Verify & activate
            </button>
        </form>
    </div>

    <div v-else class="pmc-right hide-scrollbar">
        <h1>
            Disable 2FA authentication<span></span>
        </h1>
        <div class="sec-code-key px-4 py-4 mt-4">
            <p>Authenticator app</p>
            <p class="pmc-right-p1 mt-2">Use an authentication app or browser extension to get two-factor
                authentication codes when prompted.</p>
        </div>

        <form @submit.prevent="handleDeActivate2fa" class="ver-form mt-8">
            <div>
                <div class="flex flex-col">
                    <label>Two-factor code from authenticator app</label>
                    <input v-model="verificationCode" type="number" placeholder="xxxxxx" />
                    <p v-if="verificationCodeError" class="text-red-500 text-xs mt-1 font-mono">{{
                        verificationCodeError }}
                    </p>
                </div>
            </div>
            <button class="tfa-button mt-4" type="submit">
                Verify & deactivate
            </button>
        </form>
    </div>
</template>

<script>
import QrcodeVue from 'vue-qrcode-component';

export default {
    name: 'ConfigureTwoFactorAuth',
    components: {
        QrcodeVue,
    },
    data() {
        return {
            otpauthUrl: '', // The URL you want to encode
            size: 200, // The size of the QR code (in pixels)
            secret: '',
            verificationCode: '',
            verificationCodeError: '',
        };
    },
    mounted() {
        document.getElementById('loading-bg-transparent').style.display = 'none';
        if (!this.activeUserInfo.twoFactorAuthentication) {
            this.get2FaActivationKey();
        }
    },
    computed: {
        activeUserInfo() {
            return this.$store.state.AppActiveUser;
        },
    },
    methods: {
        handleActivate2fa() {
            if (this.verificationCode === '') {
                this.verificationCodeError = 'Please enter verification code.'
                return;
            }
            this.verificationCodeError = '';
            document.getElementById('loading-bg-transparent-title').innerText = '🧑‍💻 Enabling 2-factor authentication';
            document.getElementById('loading-bg-transparent').style.display = 'flex';
            const payload = {
                key: this.secret,
                code: this.verificationCode,
            }
            this.$store
                .dispatch('auth/v2enable2fa', payload)
                .then((response) => {
                    if (response.data && response.data.twoFactorAuthentication) {
                        this.$store.dispatch('auth/v2verifyAuthToken'); //updates user data
                        this.$vs.notify({
                            title: 'Successful',
                            text: '2-factor authentication enabled!',
                            iconPack: 'feather',
                            icon: 'icon-check-circle',
                            color: 'success',
                        });
                    }
                    this.$router.push('/dashboard/rooms');
                }).catch((error) => {
                    const errorResponse = error.response?.data;
                    let message = "An error occurred.";
                    if (errorResponse?.error) {
                        message = errorResponse?.error;
                    }
                    this.$vs.notify({
                        title: 'Operation failed',
                        text: message,
                        iconPack: 'feather',
                        icon: 'icon-alert-circle',
                        color: 'danger',
                    });
                }).finally(() => {
                    document.getElementById('loading-bg-transparent').style.display = 'none';
                });
        },
        handleDeActivate2fa() {
            if (this.verificationCode === '') {
                this.verificationCodeError = 'Please enter verification code.'
                return;
            }
            this.verificationCodeError = '';
            document.getElementById('loading-bg-transparent-title').innerText = '🧑‍💻 Disabling 2-factor authentication';
            document.getElementById('loading-bg-transparent').style.display = 'flex';
            const payload = {
                code: this.verificationCode,
            }
            this.$store
                .dispatch('auth/v2disable2fa', payload)
                .then((response) => {
                    if (response.data && !response.data.twoFactorAuthentication) {
                        this.$store.dispatch('auth/v2verifyAuthToken'); //updates user data
                        this.$vs.notify({
                            title: 'Successful',
                            text: '2-factor authentication disabled!',
                            iconPack: 'feather',
                            icon: 'icon-check-circle',
                            color: 'success',
                        });
                        this.$router.push('/dashboard/rooms');
                    }
                }).catch((error) => {
                    const errorResponse = error.response?.data;
                    let message = "An error occurred.";
                    if (errorResponse?.error) {
                        message = errorResponse?.error;
                    }
                    this.$vs.notify({
                        title: 'Operation failed',
                        text: message,
                        iconPack: 'feather',
                        icon: 'icon-alert-circle',
                        color: 'danger',
                    });
                }).finally(() => {
                    document.getElementById('loading-bg-transparent').style.display = 'none';
                });
        },
        get2FaActivationKey() {
            document.getElementById('loading-bg-transparent-title').innerText = '🔍 Generating QR code';
            document.getElementById('loading-bg-transparent').style.display = 'flex';
            this.$store
                .dispatch('auth/v2get2FaActivationKey')
                .then((response) => {
                    console.log("activeUserInfo", this.activeUserInfo, response);
                    this.otpauthUrl = response.data?.otpauthUrl;
                    this.secret = response.data?.secret;
                }).catch((error) => {
                    this.$vs.notify({
                        title: 'Try again later',
                        text: "Failed to fetch activation key!",
                        iconPack: 'feather',
                        icon: 'icon-alert-circle',
                        color: 'danger',
                    });
                }).finally(() => {
                    document.getElementById('loading-bg-transparent').style.display = 'none';
                });
        },
        copyTextToClipboard(text) {
            if (navigator.clipboard) {
                navigator.clipboard.writeText(text).then(() => {
                    console.log('Text copied to clipboard!');
                    this.$vs.notify({
                        title: 'Copied!',
                        text: 'Secret copied to clipboard!',
                        iconPack: 'feather',
                        icon: 'icon-check-circle',
                        color: 'success',
                    });
                }).catch(err => {
                    console.error('Failed to copy text: ', err);
                    this.$vs.notify({
                        title: 'Error',
                        text: 'Failed to copy secret',
                        iconPack: 'feather',
                        icon: 'icon-check-circle',
                        color: 'success',
                    });
                });
            } else {
                // Fallback for older browsers
                const textarea = document.createElement('textarea');
                textarea.value = text;
                textarea.style.position = 'fixed';  // Prevent scrolling to bottom
                textarea.style.opacity = '0';
                document.body.appendChild(textarea);
                textarea.focus();
                textarea.select();
                try {
                    document.execCommand('copy');
                    this.$vs.notify({
                        title: 'Copied!',
                        text: 'Secret copied to clipboard!',
                        iconPack: 'feather',
                        icon: 'icon-check-circle',
                        color: 'success',
                    });
                } catch (err) {
                    this.$vs.notify({
                        title: 'Error',
                        text: 'Failed to copy secret',
                        iconPack: 'feather',
                        icon: 'icon-check-circle',
                        color: 'success',
                    });
                }
                document.body.removeChild(textarea);
            }
        },
    },
};
</script>

<style scoped>
*:not(i) {
    /* font-family: 'Karla', sans-serif !important; */
    font-family: 'Rethink Sans' !important;
}

.hide-scrollbar {
    scroll-behavior: smooth;
    overflow: auto;
    scrollbar-width: none;
    /* Firefox */
    -ms-overflow-style: none;
    /* IE and Edge */
}

.hide-scrollbar::-webkit-scrollbar {
    display: none;
    /* Chrome, Safari, Opera */
}

button:disabled {
    opacity: 0.5;
    cursor: not-allowed !important;
}

.sec-code-key {
    background-color: #1B1D29;
    border-radius: 12px;
    cursor: pointer;
}

.ver-form input {
    letter-spacing: 8px;
}

.qr-code-container {
    width: fit-content;
    background-color: #FFFFFF;
    border-radius: 12px;
}

.pmc-right {
    position: relative;
    overflow-y: scroll;
    width: 80%;
    height: 100%;
    padding: 70px;
}

.pmc-right p {
    color: #FFFFFF;
}

.pmc-right h1 {
    color: #FFFFFF;
    font-size: 40px;
    line-height: 120%;
}

.pmc-right h1 span {
    color: #D7DF23;
}

.pmc-right .pmc-right-p1 {
    color: #FFFFFF50;
    font-size: 14px;
}

.pmc-right label {
    font-size: 14px;
    color: #FFFFFF;
}

.pmc-right input {
    background: #15161F;
    border: 1px solid #444551;
    color: #FFFFFF;
    padding: 0.75rem !important;
    border-radius: 8px !important;
    width: 100% !important;
    outline: 2px solid transparent !important;
    outline-offset: 2px !important;
    margin-top: 6px;
}

.pmc-right button {
    background: #D7DF23;
    border: 1px solid #D7DF23;
    color: #000000;
    padding: 0.75rem;
    border-radius: 12px;
    width: 100%;
    outline: 2px solid transparent;
    outline-offset: 2px;
    cursor: pointer;
    transition: transform 0.05s ease-in-out;
}

.pmc-right button:active {
    transform: scale(0.99);
}

.password-div-wrapper {
    margin-top: 6px;
    position: relative;
}

.password-div-wrapper input {
    margin-top: 6px;
    margin-top: 0px;
}

.password-div-wrapper span {
    position: absolute;
    right: 0;
    top: 0;
    bottom: 0;
    cursor: pointer;
}

.check-box-label {
    color: #BCBCBC;
    font-size: 12px;
}

.sep-line {
    background-color: #444551;
    opacity: 0.5;
}

.sep-line-label {
    color: #FFFFFF50;
    background-color: #1B1D29;
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 5px;
}

p.text-lime-400 {
    color: #D7DF23;
    display: inline-block;
    margin-top: 12px;
}

@media (max-width: 1024px) {
    .pmc-right {
        width: 100%;
        padding: 70px 40px;
    }
}

@media (max-width: 768px) {}

@media (max-width: 480px) {
    .pmc-right {
        padding: 20px;
    }
}
</style>
